import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, Loader2, GraduationCap, Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { EmployeesTabs } from "../components/employees/EmployeesTabs";
import { ScheduleEditorDialog } from "../components/employees/ScheduleEditorDialog";
import { EventDialog } from "../components/employees/EventDialog";
import { WeekGrid } from "../components/employees/WeekGrid";
import { PlanningNavigation } from "../components/employees/PlanningNavigation";
import { MobileEventButton } from "../components/employees/MobileEventButton";
import { MobileYearSummary } from "../components/employees/MobileYearSummary";
import { getPlanningWeek, getPlanningMonth, getPlanningYear } from "../services/api";
import { useAuth } from "../context/AuthContext";

const MONTH_LABELS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

const iso = (d) => d.toLocaleDateString("fr-CA");
const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };

export default function PlanningPage() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || !!user?.can_manage_events;
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  const [view, setView] = useState(() => (searchParams.get("date") ? "day" : "week"));
  const [refDate, setRefDate] = useState(() => {
    const d = searchParams.get("date");
    return d ? new Date(d + "T00:00:00") : new Date();
  });
  const [weekData, setWeekData] = useState(null);
  const [monthData, setMonthData] = useState(null);
  const [yearData, setYearData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterEmp, setFilterEmp] = useState("all");
  const [editEmployee, setEditEmployee] = useState(null);
  const [eventDialog, setEventDialog] = useState(null);

  const monthKey = `${refDate.getFullYear()}-${String(refDate.getMonth() + 1).padStart(2, "0")}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (view === "month") {
        const res = await getPlanningMonth(monthKey);
        setMonthData(res.data);
      } else if (view === "year") {
        const res = await getPlanningYear(refDate.getFullYear());
        setYearData(res.data);
      } else {
        const res = await getPlanningWeek(iso(mondayOf(refDate)));
        setWeekData(res.data);
      }
    } catch {
      toast.error("Erreur de chargement du planning");
    } finally {
      setLoading(false);
    }
  }, [view, refDate, monthKey]);

  useEffect(() => { load(); }, [load]);

  const navigate = (delta) => {
    const d = new Date(refDate);
    if (view === "day") d.setDate(d.getDate() + delta);
    else if (view === "week") d.setDate(d.getDate() + delta * 7);
    else if (view === "month") { d.setDate(1); d.setMonth(d.getMonth() + delta); }
    else d.setFullYear(d.getFullYear() + delta);
    setRefDate(d);
  };

  const weekNum = (() => {
    const d = new Date(refDate);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
    const w1 = new Date(d.getFullYear(), 0, 4);
    return 1 + Math.round(((d - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7);
  })();

  const title = view === "day"
    ? refDate.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : view === "week"
    ? `Semaine ${weekNum} — du ${weekData ? new Date(weekData.week_start + "T00:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) : ""} au ${weekData ? new Date(new Date(weekData.week_start + "T00:00:00").getTime() + 6 * 86400000).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : ""}`
    : view === "month"
    ? refDate.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })
    : String(refDate.getFullYear());

  const employees = (weekData?.employees || []).filter((e) => filterEmp === "all" || e.id === filterEmp);

  const filteredEvents = (weekData?.events || [])
    .filter((ev) => filterEmp === "all" || (ev.employee_ids || []).length === 0 || ev.employee_ids.includes(filterEmp));

  const visibleDays = view === "week" ? (weekData?.days || []) : (weekData?.days || []).filter((d) => d.date === iso(refDate));

  return (
    <div className="planning-page p-4 lg:p-8 max-w-7xl mx-auto min-w-0" data-testid="planning-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <CalendarDays size={24} className="text-[#26788A]" /> Plannings
          </h1>
        </div>
        {canManage && (
        <button onClick={() => setEventDialog({ defaultDate: iso(refDate) })}
          className="hidden lg:flex items-center gap-2 text-sm bg-[#26788A] text-white px-4 py-2 rounded-lg hover:bg-[#1D5E6C] transition-colors" data-testid="new-event-btn">
          <Plus size={15} /> Événement / Tournage / Tâche
        </button>
        )}
      </div>

      <EmployeesTabs />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          {[["day", "Jour"], ["week", "Semaine"], ["month", "Mois"], ["year", "Année"]].map(([v, l]) => (
            <button key={v} className={`px-3 min-h-11 lg:min-h-0 py-1.5 rounded-md text-sm font-medium ${view === v ? "bg-white text-[#26788A] shadow-sm" : "text-slate-600"}`} onClick={() => setView(v)} data-testid={`planning-view-${v}`}>{l}</button>
          ))}
        </div>
        <Select value={filterEmp} onValueChange={setFilterEmp}>
          <SelectTrigger className="w-full sm:w-52 h-11 text-sm bg-white" data-testid="planning-emp-filter"><SelectValue placeholder="Filtrer par employé" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les employés</SelectItem>
            {(weekData?.employees || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <PlanningNavigation selectedDate={refDate} onDateChange={date => { setRefDate(date); if (searchParams.get("date")) setSearchParams({ date: iso(date) }); }} title={title} onPrevious={() => navigate(-1)} onNext={() => navigate(1)} onToday={() => { setRefDate(new Date()); if (searchParams.get("date")) setSearchParams({}); }} previousId="planning-prev" nextId="planning-next" titleId="planning-title" todayId="planning-today" />

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={28} className="animate-spin text-slate-300" /></div>
      ) : (view === "week" || view === "day") ? (
        !weekData?.employees?.length ? (
          <div className="text-center py-16 bg-white border border-dashed border-slate-200 rounded-xl"><p className="text-slate-500">Aucun employé actif.</p></div>
        ) : (
          <WeekGrid
            focusDate={iso(refDate)}
            days={visibleDays}
            employees={employees}
            allEmployees={weekData?.employees || []}
            events={filteredEvents}
            tasks={weekData?.tasks || []}
            canManage={canManage}
            onEventClick={(ev) => setEventDialog({ event: ev })}
            onEmployeeClick={canManage ? setEditEmployee : undefined}
            onTaskClick={() => nav("/taches")}
            onChanged={load}
          />
        )
      ) : view === "month" ? (
        <div className="bg-white border border-slate-200 rounded-xl p-3">
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-slate-400 uppercase tracking-wide mb-1">
            {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((l) => <span key={l}>{l}</span>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {monthData?.days?.length > 0 && Array.from({ length: monthData.days[0].weekday }).map((_, i) => <div key={`pad-${i}`}></div>)}
            {(monthData?.days || []).map((d) => {
              const entries = d.entries.filter((e) => filterEmp === "all" || e.employee_id === filterEmp);
              return (
                <button key={d.date} className={`min-w-0 min-h-[76px] rounded-lg border p-1 sm:p-1.5 text-left hover:border-teal-300 transition-colors ${d.is_sunday ? "bg-red-50/40 border-red-100" : d.is_holiday ? "bg-amber-50 border-amber-200" : "bg-white border-slate-100"}`}
                  onClick={() => { setRefDate(new Date(d.date + "T00:00:00")); setView("day"); }}
                  data-testid={`month-day-${d.date}`}>
                  <p className={`text-xs font-semibold ${d.is_sunday ? "text-red-500" : "text-slate-600"}`}>{Number(d.date.slice(8))}</p>
                  {d.is_holiday && <p className="text-[8px] text-amber-600 truncate">{d.holiday_name}</p>}
                  {(d.events || []).map((ev) => (
                    <span key={ev.id} className="block text-[9px] font-semibold text-white px-1.5 py-0.5 rounded mt-0.5 truncate" style={{ backgroundColor: ev.color }} title={`${ev.title}${ev.start_time ? " " + ev.start_time : ""}`}>
                      {ev.title}
                    </span>
                  ))}
                  <div className="flex flex-wrap gap-0.5 mt-1">
                    {entries.map((e) => e.absence ? (
                      <span key={e.employee_id} title={`${e.name} — ${e.absence.label}`} className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-[7px] font-bold text-white" style={{ backgroundColor: "#f43f5e" }}>A</span>
                    ) : e.is_school ? (
                      <span key={e.employee_id} title={`${e.name} — École`} className="w-3.5 h-3.5 rounded-full bg-amber-400 flex items-center justify-center"><GraduationCap size={9} className="text-white" /></span>
                    ) : (
                      <span key={e.employee_id} title={`${e.name} ${e.morning || ""} ${e.afternoon || ""}`} className="w-3.5 h-3.5 rounded-full" style={{ backgroundColor: e.color }}></span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-3 mt-3 pt-2 border-t border-slate-100">
            {(monthData?.employees || []).map((e) => (
              <span key={e.id} className="flex items-center gap-1.5 text-xs text-slate-600">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: e.color }}></span>{e.name}
              </span>
            ))}
            <span className="flex items-center gap-1.5 text-xs text-slate-600"><span className="w-3.5 h-3.5 rounded-full bg-amber-400 flex items-center justify-center"><GraduationCap size={9} className="text-white" /></span>École (alternance)</span>
          </div>
        </div>
      ) : (
        <>
        <MobileYearSummary rows={(yearData?.employees || []).filter(row => filterEmp === "all" || row.employee.id === filterEmp)} months={MONTH_LABELS} onMonthClick={i => { setRefDate(new Date(refDate.getFullYear(), i, 1)); setView("month"); }} />
        <div className="hidden xl:block bg-white border border-slate-200 rounded-xl">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
                <th className="text-left p-3">Employé</th>
                {MONTH_LABELS.map((m) => <th key={m} className="p-2 text-center">{m}</th>)}
                <th className="p-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {(yearData?.employees || []).filter((e) => filterEmp === "all" || e.employee.id === filterEmp).map((row) => (
                <tr key={row.employee.id} data-testid={`year-row-${row.employee.id}`}>
                  <td className="p-3">
                    <span className="flex items-center gap-1.5 text-sm font-medium" style={{ color: row.employee.color }}>
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: row.employee.color }}></span>
                      {row.employee.first_name} {row.employee.last_name}
                    </span>
                  </td>
                  {row.months.map((m, i) => (
                    <td key={m.month} className="p-1 text-center">
                      <button className="w-full rounded-lg py-1.5 hover:bg-slate-100 transition-colors" onClick={() => { setRefDate(new Date(refDate.getFullYear(), i, 1)); setView("month"); }}>
                        <span className="block font-mono text-xs text-slate-700">{m.hours > 0 ? `${m.hours}h` : "–"}</span>
                        {m.school_days > 0 && <span className="block text-[9px] text-amber-600"><GraduationCap size={8} className="inline -mt-0.5" /> {m.school_days}j</span>}
                        {m.absence_days > 0 && <span className="block text-[9px] text-rose-500">{m.absence_days}j abs.</span>}
                      </button>
                    </td>
                  ))}
                  <td className="p-3 text-right">
                    <span className="font-mono text-sm font-semibold text-[#26788A]">{row.total_hours}h</span>
                    {row.total_school_days > 0 && <span className="block text-[10px] text-amber-600">{row.total_school_days}j école</span>}
                    {row.total_absence_days > 0 && <span className="block text-[10px] text-rose-500">{row.total_absence_days}j absence</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {canManage && <MobileEventButton onClick={() => setEventDialog({ defaultDate: iso(refDate) })} />}
      <ScheduleEditorDialog
        open={!!editEmployee}
        onClose={() => setEditEmployee(null)}
        employee={editEmployee}
        schedule={editEmployee?.template_days}
        schoolWeeks={editEmployee?.school_weeks}
        onSaved={load}
      />

      <EventDialog
        open={!!eventDialog}
        onClose={() => setEventDialog(null)}
        event={eventDialog?.event}
        defaultDate={eventDialog?.defaultDate}
        onSaved={load}
      />
    </div>
  );
}
